import { useState, useRef, useEffect, useCallback } from 'react';

export function useReplayEngine(words, durationMs) {
  const audioRef  = useRef(null);
  const rafRef    = useRef(null);

  const [currentMs,   setCurrentMs]   = useState(0);
  const [isPlaying,   setIsPlaying]   = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  // ── rAF loop — reads audio.currentTime on every frame ─────────────────────
  const tick = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const ms = audio.currentTime * 1000;
    setCurrentMs(ms);

    if (!audio.paused && !audio.ended) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      if (audio.ended) {
        setCurrentMs(durationMs);
      }
      setIsPlaying(false);
      cancelAnimationFrame(rafRef.current);
    }
  }, [durationMs]);

  // ── Play ───────────────────────────────────────────────────────────────────
  const doPlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.play().then(() => {
      setIsPlaying(true);
      rafRef.current = requestAnimationFrame(tick);
    }).catch(() => {
      // Autoplay blocked or src missing — silently ignore
    });
  }, [tick]);

  // ── Pause ──────────────────────────────────────────────────────────────────
  const doPause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    cancelAnimationFrame(rafRef.current);
    setIsPlaying(false);
  }, []);

  // ── Toggle ─────────────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused || audio.ended) doPlay(); else doPause();
  }, [doPlay, doPause]);

  // ── Seek ───────────────────────────────────────────────────────────────────
  const seekTo = useCallback((ms) => {
    const audio = audioRef.current;
    const clamped = Math.max(0, Math.min(ms, durationMs));
    if (audio) audio.currentTime = clamped / 1000;
    setCurrentMs(clamped);
  }, [durationMs]);

  // ── Reset ──────────────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    doPause();
    seekTo(0);
  }, [doPause, seekTo]);

  // ── Buffering detection ────────────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onEnded   = () => {
      setIsPlaying(false);
      setCurrentMs(durationMs);
      cancelAnimationFrame(rafRef.current);
    };
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('ended',   onEnded);
    return () => {
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('ended',   onEnded);
    };
  }, [durationMs]);

  // ── Cleanup RAF on unmount 
  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const visibleWords = words.filter(w => w.start_s * 1000 <= currentMs);
  const activeWord   = words.find(
    w => w.start_s * 1000 <= currentMs && w.end_s * 1000 > currentMs
  );
  const isFinished   = durationMs > 0 && currentMs >= durationMs;

  return {
    audioRef,
    currentMs,
    isPlaying,
    isBuffering,
    isFinished,
    togglePlay,
    doPlay,
    seekTo,
    reset,
    visibleWords,
    activeWordId: activeWord?.id ?? null,
  };
}